import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { mapBy, pick, queue } from '@nullvoxpopuli/ember-composable-helpers';
import type { DetailedChangeset } from 'ember-changeset/types';
import PowerSelect from 'ember-power-select/components/power-select';
import set from 'ember-set-helper/helpers/set';
import { and, not } from 'ember-truth-helpers';
import DiscardedFieldTooltip from 'garaje/components/discarded-field-tooltip';
import SensitiveRevealToggle from 'garaje/components/entries/sensitive-reveal-toggle';
import SensitiveFieldShield from 'garaje/components/sensitive-field-shield';
import isRemovedString from 'garaje/helpers/is-removed-string';
import removeYour from 'garaje/helpers/remove-your';
import removedString from 'garaje/helpers/removed-string';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import italicizeIfRemoved from 'garaje/modifiers/italicize-if-removed';
import { isSensitiveFieldMasked, type SensitiveState } from 'garaje/services/sensitive-fields-visibility';

interface EntriesInputFieldSignature {
  Args: {
    changeset: DetailedChangeset<SignInFieldModel>;
    update: () => void;
    disabled?: boolean;
    isReadonly?: boolean;
    label?: string;
    sensitiveState?: SensitiveState;
  };
  Element: HTMLDivElement;
}

export default class EntriesInputField extends Component<EntriesInputFieldSignature> {
  @tracked isFocused = false;
  @tracked isSensitiveRevealed = false;

  get isSensitive(): boolean {
    return Boolean(this.args.sensitiveState);
  }

  get isSensitiveRevealable(): boolean {
    return this.args.sensitiveState === 'visible';
  }

  get isSensitiveMasked(): boolean {
    return isSensitiveFieldMasked(this.args.sensitiveState, this.isSensitiveRevealed);
  }

  toggleSensitiveRevealed = (): void => {
    this.isSensitiveRevealed = !this.isSensitiveRevealed;
  };

  get displayValue(): string | null | undefined {
    if (this.isSensitiveMasked) {
      return 'Hidden';
    }

    if (isRemovedString(this.args.changeset.value)) {
      return removedString(this.args.changeset.value);
    }

    return this.args.changeset.value;
  }

  get isDisabled(): boolean {
    return (
      this.args.disabled || this.args.isReadonly || this.isSensitiveMasked || isRemovedString(this.args.changeset.value)
    );
  }

  get showDiscardedNotice(): boolean {
    return this.args.changeset.identifier === 'name' && isRemovedString(this.args.changeset.value);
  }

  get isRequired(): boolean {
    const { required, allowVisitorRespondents, isFromAvaPolicy } = this.args.changeset;
    return required && (!allowVisitorRespondents || isFromAvaPolicy);
  }

  get errorValidation(): string[] {
    return <string[]>this.args.changeset.error?.['value']?.['validation'] || [];
  }

  <template>
    <div data-test-flow-field={{@changeset.name}} ...attributes>
      <label for={{@changeset.id}} class={{if @isReadonly "cursor-default"}}>
        {{#if @label}}
          {{@label}}
        {{else}}
          {{removeYour (if @changeset.localized @changeset.localized @changeset.name)}}
        {{/if}}
        {{#if this.isRequired}}
          <span class="requiredField" data-test-required-field></span>
        {{/if}}
        {{#if this.showDiscardedNotice}}
          <DiscardedFieldTooltip />
        {{/if}}
        {{#if this.isSensitive}}
          <SensitiveFieldShield />
        {{/if}}
      </label>
      {{#if @changeset.isSingleSelection}}
        <div
          style="margin-bottom:20px;"
          class="w-full {{if this.isSensitiveRevealable 'relative'}}"
          data-test-option-select
          {{italicizeIfRemoved @changeset.value}}
        >
          <PowerSelect
            @options={{mapBy "value" @changeset.options}}
            @selected={{this.displayValue}}
            @placeholder="Choose an option"
            @disabled={{this.isDisabled}}
            @allowClear={{true}}
            @searchEnabled={{true}}
            @triggerClass="{{if this.errorValidation 'error'}} {{if this.isSensitiveMasked 'italic !text-carbon-50'}}"
            @onChange={{queue (set @changeset "value") (fn @update @changeset)}}
            as |option|
          >
            {{option}}
          </PowerSelect>
          {{#if this.isSensitiveRevealable}}
            <SensitiveRevealToggle
              @revealed={{this.isSensitiveRevealed}}
              @onToggle={{this.toggleSensitiveRevealed}}
              class="absolute top-1/2 -translate-y-1/2 right-8"
            />
          {{/if}}
        </div>
      {{else}}
        <div class={{if this.isSensitiveRevealable "relative"}}>
          <input
            data-filter
            data-private
            id={{@changeset.id}}
            type="text"
            value={{this.displayValue}}
            disabled={{this.isDisabled}}
            class="{{if (and this.errorValidation (not this.isFocused)) 'error'}}
              {{if @isReadonly 'readonly'}}
              {{if this.isSensitiveMasked 'italic !text-carbon-50'}}
              {{if this.isSensitiveRevealable 'pr-8'}}"
            readonly={{@isReadonly}}
            {{italicizeIfRemoved @changeset.value}}
            {{on "focusin" (set this "isFocused" true)}}
            {{on "focusout" (set this "isFocused" false)}}
            {{on "input" (pick "target.value" (queue (set @changeset "value") (fn @update @changeset)))}}
          />
          {{#if this.isSensitiveRevealable}}
            <SensitiveRevealToggle
              @revealed={{this.isSensitiveRevealed}}
              @onToggle={{this.toggleSensitiveRevealed}}
              class="absolute top-1/2 -translate-y-1/2 right-2"
            />
          {{/if}}
        </div>
      {{/if}}

      {{#unless this.isFocused}}
        {{#each this.errorValidation as |errorKey|}}
          <div class="bottom-error pt-1" data-test-error>{{errorKey}}</div>
        {{/each}}
      {{/unless}}
    </div>
  </template>
}
